/**
 * Main Layout Component - OPTIMIZED WITH LAZY LOADING
 * Contains sidebar, navbar, and all retail/expense routes
 * This component is code-split from App.jsx for better performance
 */
import React, { useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useModule } from '../../contexts/ModuleContext';
import LoadingSpinner from '../common/LoadingSpinner';
import PerformanceTaskFloatingWidget from '../retail/performance-tasks/PerformanceTaskFloatingWidget';

// Eager imports (layout components - small and always needed)
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import DemoModeBanner from './DemoModeBanner';
import LowStockAlert from '../LowStockAlert';

// Import route configurations
// Route configs are small, but the pages they import will be lazy-loaded in step 2.4
import { coreRoutes } from '../../routes/coreRoutes';
import { retailRoutes } from '../../routes/retailRoutes';
import { expenseRoutes } from '../../routes/expenseRoutes';

function MainLayoutComponent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { setActiveModule, canAccessModule } = useModule();
  const location = useLocation();
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  // Sync activeModule with path on mount/location change
  // Accounting, expenses, payroll, and reports are part of the retail module
  React.useEffect(() => {
    setActiveModule('retail');
  }, [location.pathname, setActiveModule]);

  // All routes in this layout are part of the retail module
  const currentModule = 'retail';

  if (!canAccessModule(currentModule)) {
    // If they can't access current module, try to redirect them to a module they CAN access
    const allowed = ['retail', 'accounting', 'wholesale'].find((m) => canAccessModule(m));
    if (allowed) {
      return (
        <Navigate to={allowed === 'retail' ? '/dashboard' : `/${allowed}/dashboard`} replace />
      );
    }
    return <Navigate to="/select-module" replace />;
  }

  return (
    <div className="flex h-screen bg-theme-primary overflow-hidden">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden w-full lg:ml-64">
        <DemoModeBanner />
        <Navbar onMenuClick={toggleSidebar} />
        <LowStockAlert />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Routes>
            {/* Core Routes (dashboard, settings, tasks, etc.) */}
            {coreRoutes}

            {/* Retail Routes (branches, sales, products, customers) */}
            {retailRoutes}

            {/* Expense Routes (accounting, budgets, payroll, reports) */}
            {expenseRoutes}
          </Routes>
        </main>

        {/* Performance Task Floating Widget */}
        <PerformanceTaskFloatingWidget />
      </div>
    </div>
  );
}

export default MainLayoutComponent;
